/** A moment a card shows: when it is, in Unix seconds, and the IANA zone whose clock it is read on. */
export interface ShownMoment {
  at: number;
  zone: string;
}

/** Where a text shown with a moment holds it. */
export const SHOWN_MOMENT_PLACEHOLDER = '{moment}';

/** A moment as its zone's clock and calendar read it. */
interface ZonedDate {
  year: number;
  month: number;
  day: number;
  minute: number;
}

const MILLISECONDS_PER_SECOND = 1000;
const MILLISECONDS_PER_DAY = 86400000;
const DAYS_NAMED_BY_THEIR_WEEKDAY = 6;
const FALLBACK_ZONE = 'UTC';
const FALLBACK_LOCALE = 'en';
const ZONED_DATE_LOCALE = 'en-US';
const LANGUAGE_REGION_SEPARATOR = /_/g;
const TWELVE_HOUR_CYCLES: string[] = ['h11', 'h12'];
const SPACES = /\s/g;
const NON_BREAKING_SPACE = '\u00a0';
const DAY_AND_TIME_KEY = 'pmwjs_ai_chat_card_date_and_time';
const NEAR_DAY_KEYS = new Map<number, string>([
  [-1, 'pmwjs_yesterday'],
  [0, 'pmwjs_today'],
  [1, 'pmwjs_tomorrow'],
]);

/**
 * A moment as a card shows it, on its zone's clock in the user's language and with no zone: its day, then its time.
 * The day is Today, Tomorrow or Yesterday; within six days either way its weekday; else its date, with the year only
 * when it is not this year. The minutes show unless the moment is on the hour on a clock that says which half of the
 * day it is in ("5 PM"), since a 24-hour clock would otherwise show a bare hour. Day and time are worded apart and
 * joined by the language's own words, so every browser joins them the same way ("Tue, 5 PM"). A line may break
 * between the day and the time, never inside the time ("7:16 AM").
 */
export function formatFriendlyMoment(moment: ShownMoment, now: Date = new Date()): string {
  const locale = usersLocale();
  const timeZone = knownZoneOr(moment.zone);
  const date = new Date(moment.at * MILLISECONDS_PER_SECOND);
  const momentDate = zonedDateOf(date, timeZone);
  const today = zonedDateOf(now, timeZone);
  const daysFromToday = dayNumberOf(momentDate) - dayNumberOf(today);
  const nearDayKey = NEAR_DAY_KEYS.get(daysFromToday);
  const day = nearDayKey === undefined ? formatInZone(date, timeZone, locale, dateOptionsOf(momentDate, today, daysFromToday)) : window.i18next.t(nearDayKey);

  const time = keptOnOneLine(formatInZone(date, timeZone, locale, timeOptionsOf(momentDate, locale)));

  return window.i18next.t(DAY_AND_TIME_KEY, {date: day, time});
}

/** A text shown with a moment, the moment worded where the text holds SHOWN_MOMENT_PLACEHOLDER. */
export function formatTextWithMoment(text: string, moment: ShownMoment | undefined): string {
  return moment === undefined ? text : text.replace(SHOWN_MOMENT_PLACEHOLDER, formatFriendlyMoment(moment));
}

/** The chat's language as an Intl locale ("zh_CN" → "zh-CN"), or English when it is not one Intl knows. */
function usersLocale(): string {
  try {
    return Intl.getCanonicalLocales(window.i18next.language.replace(LANGUAGE_REGION_SEPARATOR, '-'))[0] ?? FALLBACK_LOCALE;
  } catch {
    return FALLBACK_LOCALE;
  }
}

function knownZoneOr(zone: string): string {
  try {
    return new Intl.DateTimeFormat(FALLBACK_LOCALE, {timeZone: zone}).resolvedOptions().timeZone;
  } catch {
    return FALLBACK_ZONE;
  }
}

function zonedDateOf(date: Date, timeZone: string): ZonedDate {
  const parts = new Intl.DateTimeFormat(ZONED_DATE_LOCALE, {timeZone, year: 'numeric', month: 'numeric', day: 'numeric', minute: 'numeric'}).formatToParts(date);
  const numberOf = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((part) => part.type === type)?.value);

  return {year: numberOf('year'), month: numberOf('month'), day: numberOf('day'), minute: numberOf('minute')};
}

function dayNumberOf({year, month, day}: ZonedDate): number {
  return Date.UTC(year, month - 1, day) / MILLISECONDS_PER_DAY;
}

function dateOptionsOf(momentDate: ZonedDate, today: ZonedDate, daysFromToday: number): Intl.DateTimeFormatOptions {
  if (Math.abs(daysFromToday) <= DAYS_NAMED_BY_THEIR_WEEKDAY) {
    return {weekday: 'short'};
  }

  return momentDate.year === today.year ? {month: 'short', day: 'numeric'} : {year: 'numeric', month: 'short', day: 'numeric'};
}

function timeOptionsOf(momentDate: ZonedDate, locale: string): Intl.DateTimeFormatOptions {
  const isOnTheHour = momentDate.minute === 0;

  return isOnTheHour && clockSaysTheHalfOfTheDay(locale) ? {hour: 'numeric'} : {hour: 'numeric', minute: '2-digit'};
}

function clockSaysTheHalfOfTheDay(locale: string): boolean {
  return TWELVE_HOUR_CYCLES.includes(new Intl.DateTimeFormat(locale, {hour: 'numeric'}).resolvedOptions().hourCycle ?? '');
}

function keptOnOneLine(text: string): string {
  return text.replace(SPACES, NON_BREAKING_SPACE);
}

function formatInZone(date: Date, timeZone: string, locale: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(locale, {...options, timeZone}).format(date);
}
